// DEPRECATED (2026-06-22): the imports below are only used by the direct Samsara/Motive ELD link
// flow, which is commented out (not deleted) in favor of the Catena universal link. If direct
// links are reintroduced, restore these together with the blocks marked DEPRECATED below.
// import { HttpStatusCode } from 'axios';
// import { useEffect, useState } from 'react';
// import { toast } from 'react-hot-toast';
// import useApi from '../../../../../utils/AxiosWrapper';
// import DirectEldRow from './widgets/DirectEldRow';

import { useCatenaConnection } from '../../../../../hooks/useCatenaConnection';
import { useFeatureFlagEnabled } from '../../../../../hooks/useFeatureFlag';
import { useUser } from '../../../../../utils/UserContext';
import CatenaIntegrationCard from './widgets/CatenaIntegrationCard';

interface IntegrationSettingsProps {
  userType: string;
  currentPage: string;
  currentTab: string;
  setCurrentTab: (tab: string) => void;
  subTab: string;
  setSubTab: (tab: string) => void;
  isPreActive: boolean;
}

function IntegrationSettings({ isPreActive }: IntegrationSettingsProps) {
  const { userRecord, userId } = useUser();

  // DEPRECATED (2026-06-22): direct Samsara/Motive ELD link state + OAuth round-trip plumbing.
  // Commented out (not deleted) so it can be restored if direct links return.
  // const { callProtectedApi } = useApi();
  // const [loadingSamsara, setLoadingSamsara] = useState(false);
  // const [linkedSamsara, setLinkedSamsara] = useState(false);
  // const [loadingMotive, setLoadingMotive] = useState(false);
  // const [linkedMotive, setLinkedMotive] = useState(false);
  //
  // const samsaraCode = localStorage.getItem('samsaraCode');
  // const samsaraScope = localStorage.getItem('samsaraScope');
  // const samsaraState = localStorage.getItem('samsaraState');
  // const motiveCode = localStorage.getItem('motiveCode');
  // const referrer = localStorage.getItem('referrer');
  // const isMobileApp =
  //   new URLSearchParams(window.location.search).get('platform') === 'mobile';

  // Flag-gated rollout: also stops the connection polling/fetches entirely while disabled.
  const catenaEnabled = useFeatureFlagEnabled('enable_catena_integration');
  const catena = useCatenaConnection(userId, catenaEnabled);

  /* DEPRECATED (2026-06-22): direct Samsara/Motive ELD OAuth round-trip + link handlers.
     Commented out (not deleted) in favor of the Catena universal link. Restore as a unit.
  useEffect(() => {
    if (isPreActive) return;
    fetchExistingELDLinks(userId ?? '');
    if (samsaraCode && samsaraState && samsaraScope) {
      linkSamsara(userId ?? '', false, samsaraState, samsaraCode);
    } else if (motiveCode) {
      linkMotive(userId ?? '', false, motiveCode);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [referrer, samsaraCode, samsaraScope, samsaraState, motiveCode]);

  const linkSamsara = async (
    customer_id: string,
    openWindow: boolean,
    samsaraState?: string,
    samsaraCode?: string
  ) => {
    setLoadingSamsara(true);
    try {
      const response = await callProtectedApi(
        '/electronicLoggingDevices/samsara/' + customer_id,
        'post',
        {},
        'Error occurred while creating Samsara record.'
      );
      const data = response?.data?.data?.eldInfo;
      if (
        data &&
        Object.entries(data)?.length > 0 &&
        (response?.status === HttpStatusCode.Ok ||
          response?.status === HttpStatusCode.Created)
      ) {
        if (openWindow) {
          localStorage.setItem('referrer', 'samsara');
          const authUrl = `https://api.samsara.com/oauth2/authorize?client_id=${process.env.REACT_APP_SAMSARA_APP_ID ?? ''}&state=${data?.samsara_id}&response_type=code&redirect_uri=${'https://' + window.location.host + '/dashboard/settings/integrations'}`;
          if (isMobileApp) {
            window.location.href = authUrl;
          } else {
            window.open(authUrl, '_blank');
          }
        } else {
          if (
            data?.samsara_id === samsaraState &&
            !data?.is_linked &&
            samsaraCode
          ) {
            await getAccessToken(data?.samsara_id, samsaraCode, 'samsara');
          } else if (data?.is_linked) {
            setLinkedSamsara(true);
          }
        }
      }
    } catch (e: any) {
      console.error('Error occurred while creating Samsara record: ', e);
    } finally {
      setLoadingSamsara(false);
    }
  };

  const linkMotive = async (
    customer_id: string,
    openWindow: boolean,
    motiveCode?: string
  ) => {
    setLoadingMotive(true);
    try {
      const response = await callProtectedApi(
        '/electronicLoggingDevices/motive/' + customer_id,
        'post',
        {},
        'Error occurred while creating Motive record.'
      );
      const data = response?.data?.data?.eldInfo;
      if (
        data &&
        Object.entries(data)?.length > 0 &&
        (response?.status === HttpStatusCode.Ok ||
          response?.status === HttpStatusCode.Created)
      ) {
        if (openWindow) {
          localStorage.setItem('referrer', 'motive');
          const authUrl = `https://api.gomotive.com/oauth/authorize?client_id=${process.env.REACT_APP_MOTIVE_CLIENT_ID ?? ''}&scope=users.read vehicles.read fuel_purchases.read inspection_reports.read hos_logs.hos_violation locations.vehicle_locations_list locations.vehicle_locations_single locations.driver_locations ifta_reports.summary utilization.driver_utilization utilization.vehicle_utilization utilization.idle_events documents.read companies.read assets.read locations.asset_locations motive_card_transactions.read motive_card_cards.read motive_card_spend_profiles.read driver_performance_events.read speeding_events.read fault_codes.read driving_periods.read scorecard_summary.read&response_type=code&redirect_uri=${process.env.REACT_APP_MOTIVE_REDIRECT_URI ?? ''}`;
          if (isMobileApp) {
            window.location.href = authUrl;
          } else {
            window.open(authUrl, '_blank');
          }
        } else {
          if (!data?.is_linked && motiveCode) {
            await getAccessToken(data?.motive_id, motiveCode, 'motive');
          } else if (data?.is_linked) {
            setLinkedMotive(true);
          }
        }
      }
    } catch (e: any) {
      console.error('Error occurred while creating Motive ELD record: ', e);
    } finally {
      setLoadingMotive(false);
    }
  };

  const fetchExistingELDLinks = async (customer_id: string) => {
    setLoadingSamsara(true);
    setLoadingMotive(true);
    try {
      const response = await callProtectedApi(
        '/electronicLoggingDevices/links/' + customer_id,
        'get',
        {}
      );
      const data = response?.data?.data?.elds;
      if (data && data?.length > 0 && response?.status === HttpStatusCode.Ok) {
        data.forEach((eld: any) => {
          if (eld?.eld_type === 'samsara' && eld?.is_linked)
            setLinkedSamsara(true);
          else if (eld?.eld_type === 'motive' && eld?.is_linked)
            setLinkedMotive(true);
        });
      }
    } catch (e: any) {
      console.error('Error occurred while fetching existing ELD links: ', e);
    } finally {
      setLoadingSamsara(false);
      setLoadingMotive(false);
    }
  };

  const getAccessToken = async (
    eld_id: string,
    link_code: string,
    eld_type: string
  ) => {
    if (eld_type === 'samsara') setLoadingSamsara(true);
    if (eld_type === 'motive') setLoadingMotive(true);
    try {
      const response = await callProtectedApi(
        `/electronicLoggingDevices/token/${eld_type}/` + eld_id,
        'post',
        { link_code, grant_type: 'authorization_code' },
        `Error occurred while linking your ${eld_type[0].toUpperCase() + eld_type.slice(1)} ELD.`
      );
      const data = response?.data?.data?.updatedEldInfo;
      if (
        data &&
        Object.entries(data)?.length > 0 &&
        (response?.status === HttpStatusCode.Ok ||
          response?.status === HttpStatusCode.Created)
      ) {
        toast.success(
          `${eld_type[0].toUpperCase() + eld_type.slice(1)} ELD Account linked successfully.`
        );
        if ((window as any).ReactNativeWebView) {
          (window as any).ReactNativeWebView.postMessage(
            JSON.stringify({ type: 'ELD_LINKED' })
          );
        }
        if (eld_type === 'samsara') {
          localStorage.removeItem('samsaraCode');
          localStorage.removeItem('samsaraState');
          localStorage.removeItem('samsaraScope');
          localStorage.removeItem('referrer');
          setLinkedSamsara(true);
        } else if (eld_type === 'motive') {
          localStorage.removeItem('motiveCode');
          localStorage.removeItem('referrer');
          setLinkedMotive(true);
        }
      }
    } catch (e: any) {
      console.error(
        `Error occurred while linking your ${eld_type} ELD Account: `,
        e
      );
    } finally {
      if (eld_type === 'samsara') setLoadingSamsara(false);
      if (eld_type === 'motive') setLoadingMotive(false);
    }
  };
  */ // END deprecated direct Samsara/Motive ELD link flow

  if (!userRecord || !userId || userId.length === 0) return null;

  return (
    <div className="flex h-full min-h-full w-full items-start justify-center overflow-y-auto overflow-x-hidden border border-[#EAECF0] bg-white p-4 sm:p-6">
      <div className="flex w-full max-w-[820px] flex-col items-start gap-y-6">
        <h2 className="text-[22px] font-semibold text-primary">Integrations</h2>

        {catenaEnabled && (
          <div className="w-full">
            <CatenaIntegrationCard
              state={catena.state}
              connectedTsps={catena.connectedTsps}
              loading={catena.loading}
              onConnect={catena.connect}
              onDisconnect={catena.disconnect}
              onSync={catena.sync}
            />
          </div>
        )}

        {/* DEPRECATED (2026-06-22): direct Samsara/Motive ELD links, replaced by the Catena
            universal link above. Commented out (not deleted) for possible restore.
        <div className="w-full">
          <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-[#98A2B3]">
            Already connected directly?
          </div>
          <div className="flex flex-col gap-3">
            <DirectEldRow
              title="Samsara ELD"
              description="Connect your Samsara ELD to enable TACH to give you more tailored financial insights."
              logoSrc="/samsara-logo.png"
              logoAlt="Samsara Logo"
              linked={linkedSamsara}
              loading={loadingSamsara}
              disabled={isPreActive}
              onLink={() => linkSamsara(userId ?? '', true)}
            />
            <DirectEldRow
              title="Motive ELD"
              description="Connect your Motive ELD to enable TACH to give you more tailored financial insights."
              logoSrc="/motive-logo.png"
              logoAlt="Motive Logo"
              linked={linkedMotive}
              loading={loadingMotive}
              disabled={isPreActive}
              onLink={() => linkMotive(userId ?? '', true)}
            />
          </div>
        </div>
        */}
      </div>
    </div>
  );
}

export default IntegrationSettings;
